import { ConsoleSink, Logger, LogLevel, parseLogLevel, resolveInitialLevel } from './Logger';

/* global window, localStorage */

export * from './Logger';

/** Prebuilt singleton logger */
export const logger = new Logger([new ConsoleSink()], resolveInitialLevel());

/**
 * Change the log level for the current session and remember it.
 *
 * DevTools on a Word task pane are awkward to reach and the pane cannot be
 * rebuilt on a user's machine, so this is exposed on `window` as
 * `sbimfLogLevel('debug')` — someone hitting a bug can turn on verbose output
 * from the console and reproduce it immediately.
 */
export function setLogLevel(level: string): void {
  const parsed = parseLogLevel(level);
  if (parsed === undefined) {
    logger.warn('Unknown log level; expected debug | info | warn | error', { requested: level });
    return;
  }
  logger.setMinLevel(parsed);
  try {
    localStorage.setItem('sbimf:logLevel', level.toLowerCase());
  } catch {
    // Not fatal — the level still applies for this session
  }
  logger.info('Log level changed', { level: level.toLowerCase() });
}

/**
 * Catch errors that never reach a try/catch: uncaught exceptions and rejected
 * promises. Without this, a crash in an event handler or a floating promise
 * leaves nothing at all in the console for the pane's own logs.
 *
 * Safe to call more than once.
 */
let handlersInstalled = false;

export function installGlobalErrorHandlers(): void {
  if (handlersInstalled || typeof window === 'undefined') return;
  handlersInstalled = true;

  window.addEventListener('error', (event: ErrorEvent) => {
    // Browsers strip details from cross-origin script errors (Office.js is
    // loaded from appsforoffice.microsoft.com), so these carry no information
    // and are not actionable.
    if (event.message === 'Script error.') return;

    logger.error('Uncaught error', {
      error: event.error ?? event.message,
      source: event.filename,
      line: event.lineno,
      column: event.colno,
    });
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason = event?.reason;
    if (typeof reason === 'string' && reason === 'Script error.') return;

    logger.error('Unhandled promise rejection', { error: reason });
  });

  // Expose the level switch for on-the-spot debugging inside Word.
  (window as unknown as Record<string, unknown>).sbimfLogLevel = setLogLevel;
}

export { LogLevel };
